import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { ManuscriptToken } from "../api/types";
import { anchored } from "../lib/anchor";
import { markGist, markLabel, type ScriptMark, type ScriptSnippet } from "../lib/scriptNotes";
import { Icon } from "./icons";

/**
 * The script's own marks, laid over the clean text in the manuscript panel.
 *
 * The text is never touched - the Formatted view measures every printed line,
 * and click-to-play counts characters - so the marks live beside it:
 *
 * - notes, comments and struck words as margin notes, each level with the word
 *   it belongs to (placed after layout: ``useMarginPlacement``);
 * - highlights and pen marks over the text as decorations on the words they
 *   touch, drawn with the CSS Custom Highlight API (``useWordDecorations``) -
 *   styling, not elements, so not a character moves. Browsers without it just
 *   show the margin notes.
 *
 * Clicking a margin note shows the original page around it (``MarkPopover``).
 */

/** Marks shown as a note in the margin; the rest only decorate words. */
export const inMargin = (m: ScriptMark) =>
  m.kind === "note" || m.kind === "comment" || m.kind === "struck" || (m.kind === "ink" && m.where === "margin");

const quoted = (t: string) => (/[“”"]/.test(t) ? t : `“${t}”`);

/** The text a margin note says. */
function noteText(m: ScriptMark): string {
  if (m.kind === "comment") return (m.parts?.length ? m.parts.map((p) => p.text).join(" · ") : m.text) || "";
  if (m.kind === "ink") return "Handwriting beside these lines";
  return m.text || "";
}

export function MarginNote({ m, onOpen, onHover }: {
  m: ScriptMark;
  onOpen: (m: ScriptMark, el: HTMLElement) => void;
  onHover: (m: ScriptMark | null) => void;
}) {
  const comment = m.kind === "comment";
  return (
    <button
      type="button"
      className={`sm-note k-${m.kind}${m.kind === "ink" ? " k-hand" : ""}`}
      data-mark={m.id}
      data-at={m.at}
      style={{ "--c": m.color } as React.CSSProperties}
      title={`${markLabel(m)} — click to see the page`}
      onClick={(e) => { e.stopPropagation(); onOpen(m, e.currentTarget); }}
      onMouseEnter={() => onHover(m)}
      onMouseLeave={() => onHover(null)}
      onFocus={() => onHover(m)}
      onBlur={() => onHover(null)}
    >
      {comment ? <Icon name="message" size={11} className="sm-note-ic" />
        : m.kind === "ink" ? <Icon name="pen-line" size={11} className="sm-note-ic" />
        : <span className="sm-note-dot" />}
      {comment && m.parts?.[0]?.who && <b className="sm-note-who">{m.parts[0].who}</b>}
      <span className="sm-note-text">
        {m.kind === "struck" ? <s>{m.text}</s> : m.kind === "note" ? quoted(noteText(m)) : noteText(m)}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Where a word is on the screen

/** A DOM range over one manuscript token's characters, or null when its
 *  paragraph isn't drawn. Skips the ‸/⏸ glyphs of added/pause flags, which
 *  aren't the manuscript's text (as the panel's own selection code does). */
export function tokenRange(root: HTMLElement, tok: ManuscriptToken): Range | null {
  const para = root.querySelector<HTMLElement>(`.ms-para[data-page="${tok.page}"][data-para="${tok.para_index}"]`);
  if (!para) return null;
  const [s, e] = tok.char_span_in_para;
  const range = document.createRange();
  let at = 0, started = false;
  const walker = document.createTreeWalker(para, NodeFilter.SHOW_TEXT);
  let n: Node | null;
  while ((n = walker.nextNode())) {
    if ((n as Text).parentElement?.closest(".caret")) continue;
    const len = (n.textContent || "").length;
    if (!started && s < at + len) { range.setStart(n, s - at); started = true; }
    if (started && e <= at + len) { range.setEnd(n, Math.max(0, e - at)); return range; }
    at += len;
  }
  return null;
}

export function useTokenIndex(tokens: ManuscriptToken[]) {
  return useMemo(() => {
    const m = new Map<number, ManuscriptToken>();
    for (const t of tokens) m.set(t.global_index, t);
    return m;
  }, [tokens]);
}

/**
 * Put each margin note level with its word, pushing a note down when the one
 * above would overlap it. Run after the panel's own layout (the Formatted
 * view's fit), and again whenever the pane is resized or its font arrives.
 */
export function useMarginPlacement(
  rootRef: RefObject<HTMLDivElement>, tokens: ManuscriptToken[], on: boolean, deps: unknown[],
) {
  const byGlobal = useTokenIndex(tokens);
  const place = () => {
    const root = rootRef.current;
    if (!root) return;
    for (const layer of root.querySelectorAll<HTMLElement>(".sm-layer")) {
      const page = layer.parentElement;
      if (!page) continue;
      const top0 = layer.getBoundingClientRect().top;
      const items = Array.from(layer.querySelectorAll<HTMLElement>(".sm-note")).map((el) => {
        const tok = byGlobal.get(Number(el.dataset.at));
        const rect = tok ? tokenRange(root, tok)?.getClientRects()[0] : undefined;
        return { el, top: rect ? rect.top - top0 - 2 : null };
      });
      items.sort((a, b) => (a.top ?? 1e9) - (b.top ?? 1e9));
      let floor = 0;
      for (const it of items) {
        if (it.top == null) { it.el.style.visibility = "hidden"; continue; }
        const t = Math.max(it.top, floor);
        it.el.style.top = `${t}px`;
        it.el.style.visibility = "visible";
        floor = t + it.el.offsetHeight + 5;
      }
    }
  };
  const placeRef = useRef(place);
  placeRef.current = place;

  // After any change that can move the text (not on every redraw: the playhead
  // redraws the report several times a second, and placing reads the layout).
  useLayoutEffect(() => { if (on) placeRef.current(); }, [on, ...deps]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !on) return;
    // The Formatted view re-fits on a timer after a resize or a font load;
    // follow it a moment later.
    let timer = 0;
    const later = () => { window.clearTimeout(timer); timer = window.setTimeout(() => placeRef.current(), 60); };
    const ro = new ResizeObserver(later);
    ro.observe(root);
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.addEventListener?.("loadingdone", later);
    later();
    return () => { ro.disconnect(); window.clearTimeout(timer); fonts?.removeEventListener?.("loadingdone", later); };
  }, [on, ...deps]);  // eslint-disable-line react-hooks/exhaustive-deps
}

// ---------------------------------------------------------------------------
// Decorations on the words

type HighlightRegistry = Map<string, unknown>;
export const registry = (): HighlightRegistry | null =>
  (typeof CSS !== "undefined" && (CSS as unknown as { highlights?: HighlightRegistry }).highlights) || null;
export const HighlightCtor = (): (new (...r: Range[]) => unknown) | null =>
  (typeof window !== "undefined" && (window as unknown as { Highlight?: new (...r: Range[]) => unknown }).Highlight) || null;

/**
 * Highlights as a highlighter's colour behind the words; pen marks over the
 * text (circles, underlines, brackets) as a wavy underline in the pen's colour;
 * and, while a margin note is hovered, its words picked out. One named
 * highlight per colour, its rule written into a style element of our own.
 */
export function useWordDecorations(
  rootRef: RefObject<HTMLDivElement>, tokens: ManuscriptToken[], marks: ScriptMark[], focus: ScriptMark | null,
  deps: unknown[],
) {
  const byGlobal = useTokenIndex(tokens);
  const names = useRef<string[]>([]);
  const style = useRef<HTMLStyleElement | null>(null);
  const ranges = useRef<Range[]>([]);
  const [stale, setStale] = useState(0);

  // A redraw that replaced the text the ranges point into (a flag's highlight
  // added or removed) leaves them pointing nowhere: draw them again.
  useLayoutEffect(() => {
    if (ranges.current.some((r) => !r.startContainer.isConnected)) setStale((n) => n + 1);
  });

  useLayoutEffect(() => {
    const reg = registry();
    const H = HighlightCtor();
    const root = rootRef.current;
    if (!reg || !H || !root) return;
    const groups = new Map<string, { rule: string; ranges: Range[] }>();
    const add = (name: string, rule: string, ids: Iterable<number>) => {
      const g = groups.get(name) ?? { rule, ranges: [] };
      for (const i of ids) {
        const tok = byGlobal.get(i);
        const r = tok ? tokenRange(root, tok) : null;
        if (r) g.ranges.push(r);
      }
      groups.set(name, g);
    };
    const span = (m: ScriptMark) => m.near.flatMap(([lo, hi]) => Array.from({ length: hi - lo + 1 }, (_, k) => lo + k));
    for (const m of marks) {
      const hex = (m.color || "#888888").replace(/[^0-9a-fA-F]/g, "").slice(0, 6).padEnd(6, "8");
      if (m.kind === "highlight")
        add(`sm-hl-${hex}`, `background-color: color-mix(in srgb, #${hex} 38%, transparent);`, span(m));
      else if (m.kind === "ink" && m.where !== "margin")
        add(`sm-ink-${hex}`, `text-decoration: underline wavy #${hex}; text-decoration-thickness: 1.5px; text-underline-offset: 3px;`, span(m));
    }
    if (focus) add("sm-focus", "background-color: color-mix(in srgb, var(--accent) 16%, transparent);", span(focus));

    for (const n of names.current) reg.delete(n);
    names.current = [];
    ranges.current = [...groups.values()].flatMap((g) => g.ranges);
    let css = "";
    for (const [name, g] of groups) {
      if (!g.ranges.length) continue;
      reg.set(name, new H(...g.ranges));
      names.current.push(name);
      css += `::highlight(${name}) { ${g.rule} }\n`;
    }
    if (!style.current) {
      style.current = document.createElement("style");
      style.current.dataset.owner = "script-marks";
      document.head.appendChild(style.current);
    }
    if (style.current.textContent !== css) style.current.textContent = css;
  }, [marks, focus, byGlobal, stale, ...deps]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => {
    const reg = registry();
    if (reg) for (const n of names.current) reg.delete(n);
    style.current?.remove();
  }, []);
}

// ---------------------------------------------------------------------------
// The original page

export function MarkPopover({ projectId, chapterId, mark, anchor, pdfUrl, onClose }: {
  projectId: string;
  chapterId: string;
  mark: ScriptMark;
  anchor: DOMRect;
  pdfUrl?: string;
  onClose: () => void;
}) {
  const { data: snip, isError } = useQuery({
    queryKey: ["script-snippet", projectId, chapterId, `mark:${mark.id}`],
    queryFn: () => api<ScriptSnippet>(
      `/api/modules/proofing/projects/${projectId}/chapters/${chapterId}/script-notes/snippet?mark=${encodeURIComponent(mark.id)}`),
    staleTime: Infinity,
    retry: false,
  });
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const away = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) onClose(); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const t = window.setTimeout(() => document.addEventListener("mousedown", away), 0);
    document.addEventListener("keydown", key);
    window.addEventListener("resize", onClose);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", key);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const said = mark.kind === "comment" ? (mark.parts?.length ? mark.parts : [{ who: "", text: mark.text || "" }]) : null;
  return (
    <div ref={box} className="sm-pop" role="dialog" aria-label={markLabel(mark)}
      style={anchored(anchor, { width: 420, min: 240, offsetLeft: anchor.width - 420, zIndex: 40 })}>
      <div className="sm-pop-head">
        <Icon name={mark.kind === "comment" ? "message" : "pen-line"} size={14}
          className={mark.kind === "comment" ? "sn-icon sn-icon-grey" : "sn-icon"} />
        <b>{markLabel(mark)}</b><span className="sn-meta">· p.{snip?.page ?? mark.page}</span>
        <span className="sn-spacer" />
        {pdfUrl && <a className="sn-open" href={`${pdfUrl}#page=${snip?.pdf_page ?? mark.pdf_page}`} target="_blank" rel="noreferrer">Open the PDF page</a>}
        <button className="sn-hide" aria-label="Close" onClick={onClose}><Icon name="x" size={14} /></button>
      </div>
      {said ? (
        <div className="sn-thread">{said.map((p, i) => <div key={i} className="sn-comment">{p.who && <b>{p.who}</b>} {p.text}</div>)}</div>
      ) : mark.kind === "note" || mark.kind === "struck" ? (
        <div className="sn-quote"><span className="sn-dot" style={{ background: mark.color }} />
          {mark.kind === "struck" ? <s>{mark.text}</s> : quoted(mark.text || "")}</div>
      ) : (
        <div className="sm-pop-gist">{markGist(mark)}</div>
      )}
      <div className="sn-snip" style={{ aspectRatio: String(snip?.aspect ?? 4.2) }}>
        {snip ? <img src={snip.image} alt="The script page around this mark" />
          : <div className="sn-wait">{isError ? "Couldn't show the page here — open the PDF page instead." : "Fetching the page…"}</div>}
      </div>
    </div>
  );
}
